'use client';

import { c as _c } from "react/compiler-runtime";
import { useLayoutEffect } from 'react';
import { getSelectedOptionsFromSearchParams, type ProductOptionLike, type ProductVariantSelectionLike } from './product-option-utils';

/**
 * Seeds variant selection from the URL's `variant` / `option.*` params on the
 * client. The server always renders the DEFAULT selection so the whole product
 * page prerenders as a static shell (reading `searchParams` on the server
 * would block prerendering under Cache Components); this hook applies the URL
 * params in a layout effect — after commit but before the browser paints — so:
 *
 * - client-side style-to-style navigations paint the URL-selected variant with
 *   no intermediate default-selection frame, and
 * - initial-load hydration always matches the prerendered default HTML (deep
 *   links briefly show the default until hydration, then correct pre-paint).
 *
 * Mount-only by design (the PDP remounts per product via `key={handle}`);
 * later URL changes come from the in-page selectors, which already own state.
 * `apply` must be referentially stable (a `setState` or `useCallback`).
 */
export function useUrlVariantSeed(variants, options, apply) {
  const $ = _c(5);
  let t0;
  let t1;
  if ($[0] !== apply || $[1] !== options || $[2] !== variants) {
    t0 = () => {
      const params = new URLSearchParams(window.location.search);
      const seeded = getSelectedOptionsFromSearchParams({
        get: name => params.get(name)
      }, variants, options);
      if (Object.keys(seeded).length > 0) {
        apply(seeded);
      }
    };
    t1 = [variants, options, apply];
    $[0] = apply;
    $[1] = options;
    $[2] = variants;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useLayoutEffect(t0, t1);
}